// covered-by: packages/schemas/src/__tests__/knowledge-activity-schema.spec.ts
import { z } from "zod";

/**
 * Append-only Knowledge Database audit trail. Insert-only for every writer
 * (christo admin actions, samba ingest, rembrandt extraction/synthesis) —
 * no update or delete path exists.
 */
export enum KnowledgeActivityAction {
  SOURCE_UPLOADED = "source_uploaded",
  SOURCE_READY = "source_ready",
  SOURCE_FAILED = "source_failed",
  SOURCE_SCOPE_CHANGED = "source_scope_changed",
  SOURCE_NOTE_CHANGED = "source_note_changed",
  SOURCE_DELETED = "source_deleted",
  SOURCE_EXTRACTION_STARTED = "source_extraction_started",
  SOURCE_EXTRACTION_FAILED = "source_extraction_failed",
  RULE_CREATED = "rule_created",
  RULE_UPDATED = "rule_updated",
  RULE_PROMOTED = "rule_promoted",
  RULE_REJECTED = "rule_rejected",
  RULE_ARCHIVED = "rule_archived",
  RULE_SCOPE_CHANGED = "rule_scope_changed",
  // Reserved for the future synthesis supersede path.
  RULE_SUPERSEDED = "rule_superseded",
}

export enum KnowledgeActivityActorKind {
  USER = "user",
  WORKFLOW = "workflow",
}

export enum KnowledgeActivityTargetType {
  SOURCE = "source",
  RULE = "rule",
}

export const knowledgeActivityActorSchema = z.object({
  kind: z.nativeEnum(KnowledgeActivityActorKind),
  userId: z.string().nullable().optional(),
  workflow: z.string().nullable().optional(),
});

export const knowledgeActivityTargetSchema = z.object({
  type: z.nativeEnum(KnowledgeActivityTargetType),
  id: z.string().min(1),
  // Snapshot (file name / rule title) so the log stays readable after the
  // target is deleted.
  label: z.string().nullable().optional(),
});

export const knowledgeActivitySchema = z.object({
  _id: z.string(),
  enterpriseId: z.string(),
  actor: knowledgeActivityActorSchema,
  action: z.nativeEnum(KnowledgeActivityAction),
  target: knowledgeActivityTargetSchema,
  change: z
    .object({
      before: z.unknown().optional(),
      after: z.unknown().optional(),
    })
    .optional(),
  createdAt: z.coerce.date(),
});

export type KnowledgeActivity = z.infer<typeof knowledgeActivitySchema>;
export type KnowledgeActivityActor = z.infer<
  typeof knowledgeActivityActorSchema
>;
export type KnowledgeActivityTarget = z.infer<
  typeof knowledgeActivityTargetSchema
>;
